import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useChooseQuoteContacts } from '~/api/sellerContacts';
import { PagedSellerContactsPicker } from '~/components/common/SellerContactsPicker';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button } from '~/components/ui';
import { sellerLabel, toastGraphQLError } from '~/utils';
import type { ContactDetails } from '~/utils/contact';
import { buyerQuoteActions } from './quoteNegotiation';
import type { Quote } from './splitAwardGridTypes';

type ContactsQuote = Pick<
  Quote,
  | 'id'
  | 'status'
  | 'requestForQuote'
  | 'sellingOrganization'
  | 'primaryContact'
  | 'recipients'
  | 'lockedRecipientIds'
>;

type QuoteContactsButtonProps = Readonly<{
  quote: ContactsQuote;
}>;

type QuoteContactsModalProps = Readonly<{
  quote: ContactsQuote;
  onClose: () => void;
}>;

function QuoteContactsModal({ quote, onClose }: QuoteContactsModalProps) {
  const { t } = useTranslation(['rfq-view', 'general']);
  const { mutateAsync: chooseQuoteContacts, isPending: isSaving } =
    useChooseQuoteContacts();
  const [selectedContactIds, setSelectedContactIds] = useState(() =>
    quote.recipients.map((recipient) => recipient.id)
  );
  const [primaryContactId, setPrimaryContactId] = useState(
    quote.primaryContact ? quote.primaryContact.id : undefined
  );
  const sellerName = sellerLabel(quote.sellingOrganization);

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const save = async (
    selectedContacts: ContactDetails[],
    primaryContact: ContactDetails
  ) => {
    try {
      await chooseQuoteContacts({
        quoteId: quote.id,
        primaryContactUserId: primaryContact.id,
        recipientUserIds: [
          ...new Set([
            ...selectedContacts.map((contact) => contact.id),
            primaryContact.id,
          ]),
        ],
      });
      toast.success(t('general:select_contacts_modal.saved'));
    } catch (error) {
      toastGraphQLError(error);
    }
    onClose();
  };

  return (
    <Modal isOpen onClose={handleClose} canClose={!isSaving}>
      <div
        className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        data-testid="select-contacts-modal"
      >
        <ModalHeader
          title={t('general:select_contacts_modal.title', { sellerName })}
          description={t('rfq_view.contacts_modal.subtitle')}
          onClose={handleClose}
          closeDisabled={isSaving}
        />
        <PagedSellerContactsPicker
          id={`quote-contacts-${quote.id}`}
          organizationId={quote.sellingOrganization.id}
          sellerName={sellerName}
          selectedContactIds={selectedContactIds}
          primaryContactId={primaryContactId}
          primaryContactMode="required"
          primaryRequiredMessage={t(
            'general:select_contacts_modal.primary_required'
          )}
          lockedContactIds={quote.lockedRecipientIds}
          lockedLabel={t('general:select_contacts_modal.acted_on_quote')}
          onChange={(contacts, chosenPrimaryContactId) => {
            setSelectedContactIds(contacts.map((contact) => contact.id));
            setPrimaryContactId(chosenPrimaryContactId);
          }}
          addFormWhenEmpty={{ onCancel: handleClose }}
          disabled={isSaving}
          testId={`quote-contacts-${quote.id}`}
          layout="modal"
          footer={({ selectedContacts, primaryContact }) => (
            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-neut-border px-6 py-4">
              <Button
                variant="secondary"
                size="sm"
                width="auto"
                onClick={handleClose}
                data-testid={`quote-contacts-${quote.id}-cancel`}
              >
                {t('general:select_contacts_modal.cancel')}
              </Button>
              <Button
                size="sm"
                width="auto"
                loading={isSaving}
                disabled={primaryContact === undefined}
                onClick={() => {
                  if (primaryContact !== undefined) {
                    void save(selectedContacts, primaryContact);
                  }
                }}
                data-testid={`quote-contacts-${quote.id}-save`}
              >
                {t('general:select_contacts_modal.confirm')}
              </Button>
            </div>
          )}
        />
      </div>
    </Modal>
  );
}

export function QuoteContactsButton({ quote }: QuoteContactsButtonProps) {
  const { t } = useTranslation(['rfq-view', 'general']);
  const [isOpen, setIsOpen] = useState(false);
  const sellerName = sellerLabel(quote.sellingOrganization);

  return (
    <>
      <button
        type="button"
        className="text-left underline decoration-dotted underline-offset-2 hover:text-prim cursor-pointer disabled:no-underline disabled:cursor-default disabled:hover:text-inherit"
        aria-label={t('rfq_view.choose_contacts_aria_label', { sellerName })}
        aria-haspopup="dialog"
        onClick={() => setIsOpen(true)}
        disabled={!buyerQuoteActions(quote).chooseContacts}
        data-testid={`quote-contacts-button-${quote.id}`}
      >
        {sellerName}
      </button>
      {isOpen && (
        <QuoteContactsModal quote={quote} onClose={() => setIsOpen(false)} />
      )}
    </>
  );
}
